// src/pages-components/MainPage/MainPage.js
import { useEffect, useState, Suspense } from 'react';
import { Component } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import styles from './MainPage.module.css';

// Динамический импорт компонентов без SSR
const SectionSlider = dynamic(() => import('./components/SectionSlider/SectionSlider'), { ssr: false });
const RecomendationsSlider = dynamic(() => import('./components/RecomendationsSlider/RecomendationSlider'), { ssr: false });
const LastUpdates = dynamic(() => import('./components/LastUpdates/LastUpdates'), { ssr: false });
const SafeYandexAd = dynamic(() => import('../Yandex-script/yandex-script'), { ssr: false });
const AdvertBanner = dynamic(() => import('../../components/AdvertBanner/AdvertBanner'), {ssr: false});



// Error Boundary для обработки ошибок в динамических компонентах
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error in dynamic component:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <div className={styles.errorMessage}>Ошибка при загрузке компонента. Пожалуйста, обновите страницу.</div>;
    }

    return this.props.children;
  }
}

const MainPage = ({ seoData }) => {
  const router = useRouter();
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://mango-read.org';
  const currentUrl = `${siteUrl}${router?.asPath || '/'}`;

  const [isMobile, setIsMobile] = useState(null);

  useEffect(() => {
    const checkMobile = () => {
      if (typeof window !== 'undefined') {
        // console.log('Window width:', window.innerWidth); // Для отладки, можно удалить в продакшене
        setIsMobile(window.innerWidth <= 768);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (isMobile === null) return null;

  // JSON-LD структурированные данные для SEO
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'ManGO',
    url: siteUrl,
    description: 'Лучшая манга на русском языке. Читайте самые популярные и новые манги онлайн.',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}/catalog?search={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };

  return (
    <>
      <Head>
        <title>ManGO - Читайте мангу онлайн на русском языке</title>
        <meta name="description" content="Лучшая коллекция манги на русском. Новые главы ежедневно, удобное чтение, рекомендации и поиск по каталогу." />
        <meta name="keywords" content="манга, читать мангу, манга онлайн, манга на русском, новые главы манги" />
        <link rel="apple-touch-icon" href="/logo/logo_mini_256_256.png" />
        <link rel="shortcut icon" href="/logo/favicon.ico" />
        
        {/* Open Graph */}
        <meta property="og:title" content="ManGO - Читайте мангу онлайн" />
        <meta property="og:description" content="Лучшая коллекция манги на русском языке с удобной читалкой. Тысячи глав, ежедневные обновления!" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:image" content={`${siteUrl}/logo/logo_mini_256_256.png`} />
        <meta property="og:image:width" content="256" />
        <meta property="og:image:height" content="256" />
        <meta property="og:image:alt" content="ManGO - Читайте мангу онлайн" />
        <meta property="og:site_name" content="ManGO" />
        <meta property="og:locale" content="ru_RU" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@mango_manga" />
        <meta name="twitter:creator" content="@mango_manga" />
        <meta name="twitter:title" content="ManGO - Читайте мангу онлайн" />
        <meta name="twitter:description" content="Лучшая коллекция манги на русском языке" />
        <meta name="twitter:image" content={`${siteUrl}/logo/logo_mini_256_256.png`} />
        <meta name="twitter:image:alt" content="ManGO - Читайте мангу онлайн" />
        
        {/* Дополнительные мета-теги */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="ManGO" />
        
        {/* Canonical URL */}
        <link rel="canonical" href={currentUrl} />
      </Head>

      {/* JSON-LD структурированные данные */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd, null, 2) }}
      />

      <div className={styles.container}>
        <ErrorBoundary>
          <Suspense fallback={<div className={styles.loadingContainer}>Загрузка главной страницы...</div>}>
            <SectionSlider 
              sectionName="Каталог" 
              sectionIcon="/underlines/Underline_catalog.svg" 
              navigate="/catalog"
            />
            
            <SectionSlider 
              sectionName="Сейчас читают" 
              sectionIcon="/underlines/Underline_read_now.svg" 
              navigate=""
            />

            <AdvertBanner />

            <SafeYandexAd 
              isMobile={isMobile} 
              blockId={isMobile ? "R-A-17126946-3" : "R-A-17126946-1"}
              type="floorAd" 
              platform="touch" 
              className="adContainer"
            />

            <LastUpdates 
              sectionName="Последние обновления" 
              sectionIcon="/underlines/Underline_Last_Updates.svg" 
            />
            
            <RecomendationsSlider />
            
            <AdvertBanner />
            
          </Suspense>
        </ErrorBoundary>
      </div>
    </>
  );
};

// SSR для SEO данных
export async function getServerSideProps() {
  const seoData = {
    lastUpdated: new Date().toISOString(),
    totalManga: 0,
    newChaptersToday: 0
  };

  return {
    props: { seoData }
  };
}

export default MainPage;